/**
 * Fee Mismatch — Needs Review, on the delivery it concerns.
 *
 * This counter record names a CENO order that also arrived from WhatsApp, and
 * the amounts differ (or both records are counting). The panel shows the three
 * numbers that matter — what the counter recorded, what the customer paid per
 * the WhatsApp record, and the difference — and lets an owner or admin record
 * what they decided. Recording a decision moves no money: a wrong amount is
 * corrected with "Correct this delivery", and a double record is cleared in
 * Approvals → Possible duplicates, each with its own reversal and audit.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Delivery } from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { date, money } from '../../lib/format';
import { Badge, Button, Select, Textarea } from '../../components/ui';

const RESOLUTION_LABELS = {
  amount_confirmed: 'The counter amount is correct',
  corrected_separately: 'Corrected with "Correct this delivery"',
  marked_duplicate: 'One record was marked a duplicate',
} as const;

export function FeeReviewSection({ delivery, timezone }: { delivery: Delivery; timezone: string }): ReactNode {
  const review = delivery.feeReview;
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [resolution, setResolution] = useState<keyof typeof RESOLUTION_LABELS>('amount_confirmed');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: () => api.post(path(`/deliveries/${delivery.id}/fee-review/resolve`), { resolution, note: note.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery'] });
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      void queryClient.invalidateQueries({ queryKey: ['fee-reviews'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not record the decision.'),
  });

  if (!review) return null;
  const currency = delivery.currencyCode ?? business?.currencyCode ?? 'NGN';
  const open = review.status === 'needs_review';
  const title = review.kind === 'fee_mismatch' ? 'Fee Mismatch — Needs Review' : 'Duplicate Reference — Needs Review';

  return (
    <section
      data-testid="fee-review"
      aria-label="Fee review"
      className={open ? 'rounded-lg border border-amber-300 bg-amber-50 p-4' : 'rounded-lg border border-slate-200 bg-white p-4'}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-slate-900">{open ? title : 'Fee review'}</h2>
        <Badge tone={open ? 'warning' : 'success'}>{open ? 'Needs review' : 'Resolved'}</Badge>
      </div>
      <p className="mt-1 text-sm text-slate-700">
        Order {delivery.dispatchRef ?? delivery.externalRef} was recorded here and also arrived from WhatsApp
        {review.otherDeliveryId ? (
          <>
            {' '}(
            <Link to={`/deliveries/${review.otherDeliveryId}`} className="font-mono text-xs text-brand-700 hover:underline">
              {review.otherReference ?? 'WhatsApp record'}
            </Link>
            )
          </>
        ) : null}
        .{' '}
        {review.kind === 'fee_mismatch'
          ? 'The amounts differ.'
          : 'Both records are counting.'}{' '}
        Nothing has been changed automatically.
      </p>
      <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-slate-500">Counter amount</dt>
          <dd className="font-medium text-slate-900">{money(review.recordedAmount, currency)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">
            Customer paid (WhatsApp{review.otherPaymentStatus ? `, ${review.otherPaymentStatus}` : ''})
          </dt>
          <dd className="font-medium text-slate-900">{money(review.verifiedAmount, currency)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Difference</dt>
          <dd className="font-medium text-slate-900">{money(review.difference, currency)}</dd>
        </div>
      </dl>

      {!open ? (
        <p className="mt-3 text-sm text-slate-700">
          {review.resolution ? RESOLUTION_LABELS[review.resolution] : 'Resolved'}
          {review.resolvedByName ? ` — ${review.resolvedByName}` : ''}
          {review.resolvedAt ? `, ${date(review.resolvedAt, timezone)}` : ''}
          {review.note ? `: ${review.note}` : ''}
        </p>
      ) : can('deliveries:approve_exceptions') ? (
        <div className="mt-3 space-y-2">
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <Select
            label="What did you decide?"
            value={resolution}
            onChange={(e) => setResolution(e.target.value as keyof typeof RESOLUTION_LABELS)}
            options={Object.entries(RESOLUTION_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <Textarea label="Note (required)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          <Button onClick={() => resolve.mutate()} loading={resolve.isPending} disabled={note.trim().length < 5}>
            Mark reviewed
          </Button>
          <p className="text-xs text-slate-500">
            This records your decision only. To change the amount use "Correct this delivery"; to stop a double record
            counting use Approvals → Possible duplicates.
          </p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-600">An owner or admin will review this.</p>
      )}
    </section>
  );
}
